import { useState, ChangeEvent, useEffect } from "react";
import AlertMessage, { AlertLevel } from "../../components/AlertMessage";
import NoDataTableBody from "../../components/NoDataTableBody";
import { SimpleOption } from "../../components/input/SimpleListbox";
import TextWithTip from "../../components/TextWithTip";
import Select from "../../components/input/Select";
import SearchFilter from "../../components/input/SearchFilter";
import { DataTablesViewModel } from "../../interfaces/dataTables";
import { useGetGradesQuery } from "../lookup/lookupApiSlice";
import {
	AvailableStudentsQueryModel,
	AvailableStudentsViewModel,
	useLazyGetAvailableStudentsQuery,
} from "./manageCourseApiSlice";

interface Props {
	seatsRemaning: number;
	limitedSeatsRemaining: boolean;
	updateCheckedIdsCallback: (checkedIds: number[]) => void;
}

export default function AddStudentsForm(props: Props) {
	const _displayLength = props.limitedSeatsRemaining ? 5 : 10;
	const initQueryargs: AvailableStudentsQueryModel = {
		search_Filter: "",
		level_Filter: "",
		iDisplayStart: 0,
		iDisplayLength: _displayLength,
		iSortCol_0: 0,
		sSortDir_0: "desc",
	};

	const [queryargs, setQueryargs] = useState(initQueryargs);
	const [page, setPage] = useState(0);
	const [searchFilter, setSearchFilter] = useState("");
	const [grade, setGrade] = useState("");
	const [gradeOptions, setGradeOptions] = useState<SimpleOption[]>([]);

	const [availableStudents, setAvailableStudents] =
		useState<DataTablesViewModel<AvailableStudentsViewModel>>();
	const [allStudentsLength, setAllStudentsLength] = useState(0);
	const [allChecked, setAllChecked] = useState(false);
	const [allPageChecked, setAllPageChecked] = useState(false);
	const [checkedIds, setCheckedIds] = useState<number[]>([]);
	const [allPageCheckboxIds, setAllPageCheckboxIds] = useState<number[]>([]);

	const gradesResult = useGetGradesQuery();
	const grades = gradesResult.data;

	const [callGetAvailableStudents, callGetAvailableStudentsResult] =
		useLazyGetAvailableStudentsQuery();

	function handleCheckboxChange(e: ChangeEvent<HTMLInputElement>) {
		if (e.target.checked) {
			setCheckedIds([...checkedIds, Number(e.target.value)]);
		} else {
			setCheckedIds(checkedIds.filter((item) => item !== Number(e.target.value)));
		}
	}

	function handleSelectAll(e: ChangeEvent<HTMLInputElement>) {
		if (e.target.checked) {
			const idsToAdd: number[] = [];
			allPageCheckboxIds.map((id) => {
				if (!checkedIds.includes(id)) {
					idsToAdd.push(id);
				}
			});
			setCheckedIds([...checkedIds, ...idsToAdd]);
		} else {
			setCheckedIds(checkedIds.filter((item) => !allPageCheckboxIds.includes(item)));
		}
	}

	const handlePageChange = (page: number) => {
		setPage(page);
	};

	function updatePageSelectAllCheckbox() {
		if (allPageCheckboxIds.every((id) => checkedIds.includes(id))) {
			setAllPageChecked(true);
		} else {
			setAllPageChecked(false);
		}
	}

	function updateQueryArgs(startValue: number) {
		setQueryargs({
			...queryargs,
			iDisplayStart: startValue,
			level_Filter: grade,
			search_Filter: searchFilter,
		});
	}

	useEffect(() => {
		updateQueryArgs(page * _displayLength);
	}, [page]);

	useEffect(() => {
		setPage(0);
		updateQueryArgs(0);
	}, [searchFilter, grade]);

	useEffect(() => {
		callGetAvailableStudents(queryargs, true)
			.unwrap()
			.then((result) => {
				setAvailableStudents(result);
			})
			.catch((error: any) => {
				console.log(error);
			});
	}, [queryargs]);

	useEffect(() => {
		props.updateCheckedIdsCallback(checkedIds);

		if (checkedIds.length === allStudentsLength && allStudentsLength != 0) {
			setAllChecked(true);
		} else {
			setAllChecked(false);
		}
		updatePageSelectAllCheckbox();
	}, [checkedIds]);

	useEffect(() => {
		updatePageSelectAllCheckbox();
	}, [allPageCheckboxIds]);

	useEffect(() => {
		const _temp: number[] = [];

		availableStudents?.aaData.map((row) => {
			_temp.push(Number(row.dT_RowId));
		});
		setAllPageCheckboxIds(_temp);
		setAllStudentsLength(availableStudents?.iTotalDisplayRecords ?? 0);
	}, [availableStudents]);

	useEffect(() => {
		const options: SimpleOption[] = [{ value: "", label: "All Grades" }];
		if (grades) {
			grades?.map((grade) => {
				options.push({ value: grade.gradeId.toString(), label: grade.name });
			});
		}
		setGrade(options[0].value.toString());
		setGradeOptions(options);
	}, [grades]);

	return (
		<div>
			{props.limitedSeatsRemaining && (
				<AlertMessage
					className="mt-4 text-sm"
					type={AlertLevel.Warning}
					message={`There are only ${props.seatsRemaning} seat(s) remaining. Any existing students not currently enrolled in a course will need a license. To request additional student seats for your school, please contact your school administrator.`}
					showIcon={true}
				/>
			)}
			<p className="font-semibold text-sm pt-3">Filters:</p>
			<div className="mt-2 flex flex-row gap-2">
				<SearchFilter
					id="searchStudents"
					name="searchStudents"
					htmlFor="searchStudents"
					searchFilter={searchFilter}
					setSearchFilter={setSearchFilter}
					maxLength={125}
					label="Search Students"
					hiddenLabel
					placeHolder="Search Students"
					className="w-1/2"
				/>
				<Select
					value={grade}
					setValue={(v) => setGrade(v as string)}
					simpleListBoxId="gradeFilter"
					options={gradeOptions}
					fieldClasses="w-1/2"
					simpleListBoxClasses="w-full"
				/>
			</div>
			<div className="mt-2">
				<div className="flex justify-between items-end">
					<p className="font-semibold text-sm p-1">
						{checkedIds.length} students selected
					</p>
					<button
						className="text-xs p-1 hover:cursor-pointer focus:outline-hidden"
						onClick={() => setCheckedIds([])}>
						[clear all]
					</button>
				</div>
				<table className="tbl table-fixed tbl-data">
					<thead>
						<tr>
							<th className="w-1/12">
								<input
									id="selectAllCheckbox"
									type="checkbox"
									className="size-4 rounded-sm border-gray-300 text-primary-600 focus:ring-primary-600"
									onChange={(e) => handleSelectAll(e)}
									checked={allChecked || allPageChecked}
								/>
							</th>
							<th className="w-6/12">Name</th>
							<th className="w-5/12">Username</th>
						</tr>
					</thead>

					{availableStudents?.aaData.length == 0 ? (
						<NoDataTableBody columnCount={3} message="No matching records found" />
					) : (
						<tbody>
							{availableStudents?.aaData.map((row) => {
								return (
									<tr key={"student-" + row.dT_RowId}>
										<td>
											<input
												id={"student-" + row.dT_RowId}
												value={row.dT_RowId}
												type="checkbox"
												checked={checkedIds.includes(Number(row.dT_RowId))}
												className="size-4 rounded-sm border-gray-300 text-primary-600 focus:ring-primary-600"
												onChange={(e) => handleCheckboxChange(e)}
											/>
										</td>
										<td>
											<TextWithTip
												uniqueName={`name-${row.dT_RowId}`}
												text={row.name}
											/>
										</td>
										<td>
											<TextWithTip
												uniqueName={`username-${row.dT_RowId}`}
												text={row.username}
											/>
										</td>
									</tr>
								);
							})}
						</tbody>
					)}

					{(availableStudents?.iTotalDisplayRecords ?? 0) > queryargs.iDisplayLength && (
						<tfoot>
							<tr>
								<td colSpan={3} className="!px-2">
									<Pagination
										itemCount={availableStudents?.iTotalDisplayRecords}
										page={page}
										pageSize={queryargs.iDisplayLength}
										onChange={(page) => handlePageChange(page)}
									/>
								</td>
							</tr>
						</tfoot>
					)}
				</table>
			</div>
		</div>
	);
}

interface PaginationProps {
	pageSize: number;
	page: number;
	itemCount: number | undefined;
	onChange: (page: number) => void;
}

function Pagination({ pageSize, page, itemCount, onChange }: PaginationProps) {
	if (!itemCount || itemCount <= pageSize) {
		return null;
	}
	const pages = Math.ceil(itemCount / pageSize);
	const first = page * pageSize + 1;
	const last = (page + 1) * pageSize > itemCount ? itemCount : (page + 1) * pageSize;

	return (
		<div className="flex items-center justify-between bg-white">
			<div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
				<div>
					<p className="text-sm text-gray-700">
						Showing <span className="font-medium">{first}</span> to{" "}
						<span className="font-medium">{last}</span> of{" "}
						<span className="font-medium">{itemCount}</span> results
					</p>
				</div>
				<div>
					<nav
						className="isolate inline-flex space-x-1 rounded-md shadow-sm"
						aria-label="Pagination">
						<button
							className="btn btn-naked border !shadow-md ml-4 mt-2"
							disabled={page <= 0}
							onClick={() => onChange(--page)}>
							Previous
						</button>
						<button
							className="btn btn-naked border !shadow-md ml-4 mt-2"
							disabled={page >= pages - 1}
							onClick={() => onChange(++page)}>
							Next
						</button>
					</nav>
				</div>
			</div>
		</div>
	);
}
